/**
 * A dropdown you can search.
 *
 * A native `<select>` stops being usable somewhere around a dozen options, and an agency with
 * four hundred tenants has to scroll a list of four hundred names to record a payment. Past
 * eight or so rows the control has to be searchable, and past a few hundred the search has to
 * happen on the server — sending the whole client list to the browser to filter it is the same
 * mistake one layer along.
 *
 * So this does both:
 *
 * - `options` — a fixed list (currencies, timezones), filtered in the browser.
 * - `search` — a function that asks the API, debounced, with the request that came back last
 *   winning rather than the one that finished last.
 *
 * It stays a real form control: it renders a hidden `<input name=…>`, so uncontrolled forms that
 * read `FormData` keep working unchanged.
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { IconChevronDown, IconClose, IconSearch } from "./icons";

export interface Option {
  value: string;
  label: string;
  /** A second line — a phone number, the property a unit belongs to. */
  hint?: string;
  /** A short tag on the right, such as a status. */
  tag?: string;
  /** A mark on the left — the property-type icon, so a picker reads like the list it came from. */
  icon?: ReactNode;
}

export interface SearchSelectProps {
  value: string;
  onChange: (value: string, option: Option | null) => void;
  /** A fixed list. Filtered in the browser. */
  options?: Option[];
  /** Ask the server. Called debounced; whatever it resolves to is the list. */
  search?: (query: string) => Promise<Option[]>;
  /** Shown when nothing is selected. */
  placeholder?: string;
  /** Needed when `search` is used and the page loads with a value already chosen. */
  selectedOption?: Option | null;
  /**
   * Asks the server what one id is called. Used when the form opens on a saved value and the
   * caller has only the id — without it the control would have nothing to show but the id
   * itself, which is never the right answer.
   */
  resolve?: (value: string) => Promise<Option | null>;
  disabled?: boolean;
  required?: boolean;
  /** Renders a hidden input so `FormData` sees the value. */
  name?: string;
  id?: string;
  emptyMessage?: string;
  /** Below this many options, the search box is hidden — it would only be in the way. */
  searchThreshold?: number;
  className?: string;
  /**
   * Draw the closed control yourself.
   *
   * For controls that are narrower than what they select from: the dialling picker shows a flag
   * and `+234`, while its list shows every country by name. Without this the button would either
   * truncate the name to nothing or force the field to be as wide as "Bosnia and Herzegovina".
   */
  renderValue?: (option: Option | null) => ReactNode;
  /** Width of the open list, when it should not match the button. */
  menuClassName?: string;
}

export function SearchSelect({
  value,
  onChange,
  options,
  search,
  placeholder = "Choose…",
  selectedOption,
  resolve,
  disabled,
  required,
  name,
  id,
  emptyMessage = "Nothing matched.",
  searchThreshold = 8,
  className = "",
  renderValue,
  menuClassName = "w-full",
}: SearchSelectProps) {
  const generatedId = useId();
  const controlId = id ?? generatedId;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [remote, setRemote] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(0);
  /** The last option we displayed for the current value, so the button keeps its label. */
  const [resolved, setResolved] = useState<Option | null>(selectedOption ?? null);
  const [resolving, setResolving] = useState(false);

  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const requestId = useRef(0);

  useEffect(() => {
    if (selectedOption) setResolved(selectedOption);
  }, [selectedOption]);

  /**
   * Label a value the control was handed rather than chosen.
   *
   * Runs only when nothing else can name it, and only once per value. An id shown to a user is
   * always a defect, so while this is in flight the control says nothing rather than saying the id.
   */
  const known =
    (resolved?.value === value ? resolved.label : undefined) ??
    options?.find((option) => option.value === value)?.label;

  useEffect(() => {
    if (!value || known || !resolve) return;
    let live = true;
    setResolving(true);
    resolve(value)
      .then((option) => {
        if (live && option) setResolved(option);
      })
      .catch(() => {})
      .finally(() => {
        if (live) setResolving(false);
      });
    return () => {
      live = false;
    };
  }, [value, known, resolve]);

  /* ------------------------------------------------------------- the list */

  const localMatches = useMemo(() => {
    if (!options) return [];
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(needle) || (option.hint ?? "").toLowerCase().includes(needle),
    );
  }, [options, query]);

  const list = search ? remote : localMatches;

  const runSearch = useCallback(
    async (text: string) => {
      if (!search) return;
      // A slow response for "ada" must not overwrite a fast one for "adaeze". The counter makes
      // the *latest* request win rather than the last to arrive.
      const ticket = ++requestId.current;
      setLoading(true);
      try {
        const results = await search(text);
        if (ticket === requestId.current) setRemote(results);
      } catch {
        if (ticket === requestId.current) setRemote([]);
      } finally {
        if (ticket === requestId.current) setLoading(false);
      }
    },
    [search],
  );

  useEffect(() => {
    if (!open || !search) return;
    const timer = window.setTimeout(() => void runSearch(query.trim()), query.trim() ? 220 : 0);
    return () => window.clearTimeout(timer);
  }, [open, query, search, runSearch]);

  /* ------------------------------------------------------- open and close */

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  useEffect(() => {
    if (open) {
      setHighlight(0);
      window.setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setQuery("");
    }
  }, [open]);

  const choose = (option: Option) => {
    setResolved(option);
    onChange(option.value, option);
    setOpen(false);
  };

  const clear = () => {
    setResolved(null);
    onChange("", null);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlight((current) => Math.min(current + 1, list.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlight((current) => Math.max(current - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      const option = list[highlight];
      if (option) choose(option);
    } else if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
    }
  };

  const showSearch = Boolean(search) || (options?.length ?? 0) >= searchThreshold;
  // An id is not a label. If the name is not known yet, the control waits rather than exposing it.
  const label = known ?? "";
  const shownLabel = label || (value ? (resolving || resolve ? "Loading…" : "") : "");

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      {name ? <input type="hidden" name={name} value={value} /> : null}

      <button
        type="button"
        id={controlId}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={`field flex items-center gap-2 text-left ${disabled ? "" : "cursor-pointer"}`}
      >
        {renderValue ? (
          <span className="min-w-0 flex-1">{renderValue(resolved?.value === value ? resolved : null)}</span>
        ) : (
          <>
            {label && resolved?.value === value && resolved.icon ? (
              <span className="flex shrink-0 items-center">{resolved.icon}</span>
            ) : null}
            <span className={`min-w-0 flex-1 truncate ${label ? "" : "text-ink-400"}`}>{shownLabel || placeholder}</span>
          </>
        )}
        {value && !required && !disabled ? (
          <span
            role="button"
            tabIndex={-1}
            aria-label="Clear"
            onClick={(event) => {
              event.stopPropagation();
              clear();
            }}
            className="shrink-0 text-ink-400 hover:text-ink-700"
          >
            <IconClose size={14} />
          </span>
        ) : null}
        <IconChevronDown size={16} className="shrink-0 text-ink-400" />
      </button>

      {open ? (
        <div
          className={`absolute z-30 mt-1 ${menuClassName} overflow-hidden rounded-[var(--radius-surface)] border border-ink-200 bg-white`}
          style={{ boxShadow: "var(--shadow-menu)" }}
        >
          {showSearch ? (
            <div className="flex items-center gap-2 border-b border-ink-100 px-3 py-2">
              <IconSearch size={15} className="shrink-0 text-ink-400" />
              <input
                ref={inputRef}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Type to search…"
                className="w-full border-0 bg-transparent p-0 text-sm outline-none placeholder:text-ink-400"
              />
              {loading ? <span className="size-3.5 shrink-0 animate-spin rounded-full border-2 border-ink-200 border-t-brand-600" /> : null}
            </div>
          ) : null}

          <ul role="listbox" className="scroll-area max-h-64 overflow-y-auto py-1">
            {list.length === 0 ? (
              <li className="px-3 py-6 text-center text-sm text-ink-500">
                {loading ? "Searching…" : query ? emptyMessage : "Start typing to search."}
              </li>
            ) : (
              list.map((option, index) => (
                <li key={option.value}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={option.value === value}
                    onMouseEnter={() => setHighlight(index)}
                    onClick={() => choose(option)}
                    className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm ${
                      index === highlight ? "bg-ink-50" : ""
                    } ${option.value === value ? "font-semibold text-brand-700" : "text-ink-800"}`}
                  >
                    {option.icon ? <span className="flex shrink-0 items-center">{option.icon}</span> : null}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{option.label}</span>
                      {option.hint ? <span className="block truncate text-xs text-ink-500">{option.hint}</span> : null}
                    </span>
                    {option.tag ? <span className="shrink-0 text-xs text-ink-400">{option.tag}</span> : null}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
